import PropTypes from "prop-types";
import { memo, useState, useEffect, useRef } from "react";
import Paragraphs from "app/pages/.shared/Paragraphs";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { ProductsCarousel } from "app/pages/.shared/ProductsCarousel";
import IconRight from "app/pages/.shared/static/icons/IconRight";
import ProductCard from "app/pages/.shared/ProductCard/ProductCard";
import ProductList from "app/pages/Listing/ProductList/ProductList";
import classNames from "classnames";
import "./ProductListBlock.scss";
import Button, {
	BUTTON_ICON_ALIGNMENTS,
	BUTTON_SIZES,
	BUTTON_VARIANTS,
} from "app/pages/.shared/form/Button";
import { FormattedMessage } from "react-intl";
import { useFacets } from "app/features/product/productFilters";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { useIntersectionObserver } from "app/hooks";
import { ActionBar } from "app/features/product/productActionBar/ActionBar";
import { useSortedProducts } from "app/features/product/productSort";
import { sendTagOnProductsFilterOpen } from "app/utils/analytics";

const PRODUCTS_PER_CHUNK = 12;

const ProductListBlock = ({
	title,
	shortVersion,
	shouldHideProductPriceBanner,
	redirectUrl,
	displayFilters = false,
	isMobile,
}) => {
	const { track } = useAnalytics();

	const topOfProductListRef = useRef();

	const [loadMoreRef, loadMoreRefInView] = useIntersectionObserver({
		rootMargin: "0px 0px 600px 0px",
	});

	const { filteredProducts, activeFilters } = useFacets();
	const { sortedProducts } = useSortedProducts(filteredProducts);

	const [visibleAccommodationsCount, setVisibleAccommodationsCount] = useState(
		PRODUCTS_PER_CHUNK
	);
	const visibleProducts = sortedProducts?.slice(0, visibleAccommodationsCount);
	const hasMoreToShow = sortedProducts?.length > visibleAccommodationsCount;

	const productListBLockTitleClassNames = classNames({
		"product-list-block__title": true,
		"product-list-block__title--with-filter": displayFilters,
	});

	const handleFilterOpen = (filterId, isSticky) => {
		sendTagOnProductsFilterOpen({
			filterId,
			...(!isMobile &&
				typeof isSticky === "boolean" && { filterPosition: isSticky ? "sticky" : "top" }),
		});
	};

	const handleMobileFilterOpen = isSticky => {
		track("listingPage_filters_mobile_open", {
			filterPosition: isSticky ? "sticky" : "top",
		});
	};

	const handleSortOpen = isSticky => {
		track("user_click_sort-button", {
			sortPosition: isSticky ? "sticky" : "top",
		});
	};

	const handleSortChange = sortById => {
		track("sort_product_list", { sortby: sortById });
	};

	const handleScrollToTop = () => {
		if (!topOfProductListRef.current) {
			return;
		}

		requestAnimationFrame(() => {
			topOfProductListRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		});
	};

	useEffect(() => {
		if (!loadMoreRefInView) {
			return;
		}

		if (hasMoreToShow) {
			setVisibleAccommodationsCount(prev => prev + PRODUCTS_PER_CHUNK);
		}
	}, [loadMoreRefInView, hasMoreToShow]);

	useEffect(() => {
		setVisibleAccommodationsCount(PRODUCTS_PER_CHUNK);
	}, [activeFilters]);

	let productsNode = null;

	if (isMobile) {
		if (shortVersion) {
			productsNode = (
				<ProductsCarousel
					products={visibleProducts}
					hidePriceBanner={shouldHideProductPriceBanner}
					noSwiping={!isMobile}
				/>
			);
		} else {
			productsNode = (
				<ProductList
					products={visibleProducts}
					hidePriceBanner={shouldHideProductPriceBanner}
					hideMembershipBlock={true}
				/>
			);
		}
	} else if (shortVersion) {
		productsNode = (
			<div className="product-list-block__products">
				{visibleProducts.map((product, index) => {
					return (
						<div key={product.id} data-testid={`product-${product.uri}`}>
							<ProductCard
								product={product}
								position={index}
								hidePriceBanner={shouldHideProductPriceBanner}
							/>
						</div>
					);
				})}
			</div>
		);
	} else {
		productsNode = (
			<ProductList
				products={visibleProducts}
				hidePriceBanner={shouldHideProductPriceBanner}
				hideMembershipBlock={true}
			/>
		);
	}

	return (
		<div className="product-list-block" data-testid="product-list-block">
			<div className="product-list-block__container">
				<div className={productListBLockTitleClassNames}>
					{title && (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.XL3}
							variantMD={TYPOGRAPHY_VARIANTS.XL4}
							isBold
							component="h2"
						>
							<Paragraphs paragraphs={title} enableHTMLFormatting />
						</Typography>
					)}

					{!isMobile && shortVersion && (
						<div className="product-list-block__view-all-offers-link">
							<Button
								variant={BUTTON_VARIANTS.BORDERLESS}
								size={BUTTON_SIZES.LARGE}
								to={redirectUrl}
								label={
									<FormattedMessage id="products.carousel.view.all.offers.label" />
								}
								alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
								icon={<IconRight />}
							/>
						</div>
					)}
				</div>
			</div>

			{displayFilters && !shortVersion && (
				<>
					<div ref={topOfProductListRef} aria-hidden="true" />

					<ActionBar
						showMiniSearch={false}
						showMapButton={false}
						isLoading={false}
						onFilterOpen={handleFilterOpen}
						onFilterTriggerMobileOpen={handleMobileFilterOpen}
						onSortOpen={handleSortOpen}
						onSubmit={handleScrollToTop}
						onSortChange={handleSortChange}
					/>
				</>
			)}

			<div className="product-list-block__container">
				{productsNode}

				<div ref={loadMoreRef} aria-hidden="true" />

				{isMobile && shortVersion && (
					<div className="product-list-block__view-all-offers-button">
						<Button
							variant={BUTTON_VARIANTS.STROKE}
							size={BUTTON_SIZES.LARGE}
							to={redirectUrl}
							label={
								<FormattedMessage id="products.carousel.view.all.offers.label" />
							}
							alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
							icon={<IconRight />}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

ProductListBlock.propTypes = {
	title: PropTypes.string,
	redirectUrl: PropTypes.string,
	shouldHideProductPriceBanner: PropTypes.bool,
	shortVersion: PropTypes.bool,
	displayFilters: PropTypes.bool,
	isMobile: PropTypes.bool,
};

export default memo(ProductListBlock);
